/** Daily work, read from existing tenant-scoped endpoints. */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { ApiException } from "../lib/api";
import { getApiErrorMessage, normalizeLang } from "../lib/apiErrorMessage";
import { useIntakeEvents } from "../lib/queries/intakeLinks";
import { useProjects } from "../lib/queries/projects";

import { InProgressRequests } from "./_parts/InProgressRequests";
import { PriorityActionsSection } from "./_parts/PriorityActionsSection";
import { EtatOperationnelBadge } from "./projects/_parts/EtatOperationnel";
const RECENT_LIMIT = 5;
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { user, tenant } = useAuth();
  const projects = useProjects({ limit: RECENT_LIMIT });
  const pendingEvents = useIntakeEvents({ status: "pending_classification", limit: RECENT_LIMIT });
  const name = user?.full_name?.trim() || tenant?.name || "";
  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            {t("nav.dashboard")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {name ? t("dashboard.hero.greeting", { name }) : t("dashboard.hero.greeting_anonymous")}
          </p>
        </div>
        <Link to="/requests/new" className="btn-primary inline-flex min-h-11 items-center">
          {t("nav.new_request")}
        </Link>
      </header>
      <PriorityActionsSection />
      <InProgressRequests />
      <section
        aria-label={t("dashboard.recent.section_label")}
        className="mt-6 grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2"
      >
        <RecentEventsCard query={pendingEvents} lang={lang} />
        <RecentProjectsCard query={projects} lang={lang} />
      </section>
      <nav aria-label={t("dashboard.actions.section_label")} className="mt-6 flex flex-wrap gap-4">
        <Link to="/companies/new" className="btn min-h-11">
          {t("dashboard.actions.create_company")}
        </Link>
        <Link to="/intake-events/new" className="btn min-h-11">
          {t("requests.new.cards.from-pdf.cta")}
        </Link>
      </nav>
    </div>
  );
}

type EventRowShape = {
  id: string;
  intake_link_id: string | null;
  created_at: string;
  payload: Record<string, unknown>;
};

function RecentEventsCard({
  query,
  lang,
}: {
  query: ReturnType<typeof useIntakeEvents>;
  lang: "fr" | "en";
}) {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t("dashboard.recent.events.title")}
      className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-base font-semibold text-slate-900">
        {t("dashboard.recent.events.title")}
        {query.data && (
          <span className="ml-2 text-sm font-normal tabular-nums text-slate-500">
            {query.data.items.length}
            {query.data.next_cursor ? "+" : ""}
          </span>
        )}
      </h2>
      <div className="mt-3">
        {query.isLoading && !query.data ? (
          <ListSkeleton />
        ) : query.error ? (
          <RecentError lang={lang} error={query.error} />
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">{t("dashboard.recent.events.empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {query.data!.items.slice(0, RECENT_LIMIT).map((event) => {
              const evt = event as unknown as EventRowShape;
              const payload = evt.payload as {
                comment?: string | null;
                email?: string | null;
              };
              const comment = (payload.comment ?? "").trim();
              const email = (payload.email ?? "").trim();
              const headline = comment
                ? comment.length > 60
                  ? `${comment.slice(0, 60).trimEnd()}…`
                  : comment
                : t("dashboard.recent.events.no_comment");
              // F2-V1 : tout intake_event (soumission publique liée OU orphelin
              // sans intake_link_id) ouvre son détail `/intake-events/:id` — la
              // surface classify + wizard de revue. Auparavant on routait vers la
              // page de gestion du lien (`/intake-links/:id`) ou la liste, d'où
              // l'impossibilité de consulter/traiter la réponse reçue.
              const linkPath = `/intake-events/${evt.id}`;
              return (
                <li key={evt.id}>
                  <Link
                    to={linkPath}
                    className="focus-visible:bg-brand-50 focus-visible:ring-brand-500 flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-[-2px]"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-slate-800">{headline}</p>
                      {email && <p className="truncate text-xs text-slate-500">{email}</p>}
                    </div>
                    <time
                      dateTime={evt.created_at}
                      className="shrink-0 text-xs tabular-nums text-slate-500"
                    >
                      {formatDateShort(evt.created_at, lang)}
                    </time>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function RecentProjectsCard({
  query,
  lang,
}: {
  query: ReturnType<typeof useProjects>;
  lang: "fr" | "en";
}) {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t("dashboard.recent.projects.title")}
      className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-base font-semibold text-slate-900">
        {t("dashboard.recent.projects.title")}
      </h2>
      <div className="mt-3">
        {query.isLoading && !query.data ? (
          <ListSkeleton />
        ) : query.error ? (
          <RecentError lang={lang} error={query.error} />
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">{t("dashboard.recent.projects.empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {query.data!.items.slice(0, RECENT_LIMIT).map((p) => {
              const proj = p;
              return (
                <li key={proj.id}>
                  <Link
                    to={`/projects/${proj.id}`}
                    className="focus-visible:bg-brand-50 focus-visible:ring-brand-500 flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-[-2px]"
                  >
                    <div className="min-w-0 flex-1">
                      {/* 9.BH.1 — le client, puis le dossier, puis ce qu'il
                          attend. Le code de référence et la date de création
                          n'aidaient à choisir aucun dossier. */}
                      {proj.company_legal_name && (
                        <p className="truncate text-xs text-slate-500">{proj.company_legal_name}</p>
                      )}
                      <p className="truncate text-slate-800">
                        {proj.title || t("dashboard.recent.projects.untitled")}
                      </p>
                    </div>
                    <EtatOperationnelBadge
                      etat={proj.operational_state}
                      activityScope={proj.activity_scope}
                      enRetard={proj.is_late}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function ListSkeleton() {
  return (
    <ul aria-busy="true" className="space-y-2">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex items-center justify-between gap-3 py-2">
          <div className="h-4 flex-1 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="h-3 w-16 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
        </li>
      ))}
    </ul>
  );
}

function RecentError({ lang, error }: { lang: "fr" | "en"; error: unknown }) {
  const { t } = useTranslation();
  const msg =
    error instanceof ApiException
      ? getApiErrorMessage(error, lang) || t("dashboard.error.partial")
      : t("dashboard.error.partial");
  return (
    <p
      role="alert"
      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
    >
      {msg}
    </p>
  );
}

function formatDateShort(iso: string, lang: "fr" | "en"): string {
  try {
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", {
      month: "short",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
